import React, { useState, useCallback } from 'react';
import { METRIC_DEFINITIONS } from '../../constants/metricDefinitions';
import type { TriviaQuestion } from '../../types/triviaRush';

const METRIC_KEY_MAP: Record<string, string> = {
  'Market Cap': 'marketCapitalization',
  'Annual Revenue': 'annualRevenue',
  'Net Income': 'netIncome',
  'Gross Profit Margin': 'grossMargin',
  'Net Margin': 'profitMargin',
  'P/E Ratio': 'trailingPE',
};

interface RushGameOverProps {
  score: number;
  onPlayAgain: () => void;
  error?: string | null;
  lastMetricName?: string;
  lastQuestion?: TriviaQuestion | null;
  industryContextA?: string;
  industryContextB?: string;
}

export const RushGameOver: React.FC<RushGameOverProps> = ({
  score,
  onPlayAgain,
  error,
  lastMetricName,
  lastQuestion,
  industryContextA,
  industryContextB,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopyResults = useCallback(async () => {
    const squares = '🟩'.repeat(score) + '🟥';
    const text = `I got ${score} right in Wallstreetle's Trivia Rush! How many can you get right?\n${squares}\nwww.wallstreetle.com/trivia-rush`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers / non-HTTPS
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [score]);

  const hasIndustryContext = industryContextA || industryContextB;

  return (
    <div className="flex flex-col items-center justify-center min-h-[80vh] px-6 text-center">
      <h2
        className="text-3xl sm:text-4xl font-bold tracking-tight mb-2"
        style={{ color: 'var(--rush-wrong)', fontFamily: 'DM Serif Display, serif' }}
      >
        GAME OVER
      </h2>

      <p className="text-sm mb-6" style={{ color: 'hsl(var(--muted-foreground))' }}>
        Your score
      </p>

      <div
        className="text-6xl sm:text-7xl font-bold mb-10 font-mono"
        style={{ color: 'hsl(var(--foreground))' }}
      >
        {score}
      </div>

      {error && (
        <p className="text-sm mb-6" style={{ color: 'var(--rush-warning)' }}>
          {error}
        </p>
      )}

      <div className="flex flex-col gap-3 w-full max-w-xs">
        <button
          onClick={handleCopyResults}
          className="flex items-center justify-center gap-2 px-10 py-4 rounded-full text-lg font-bold transition-all duration-200 hover:scale-105 active:scale-95"
          style={{
            backgroundColor: copied ? '#16a34a' : '#22c55e',
            color: '#fff',
            boxShadow: '0 0 20px rgba(34, 197, 94, 0.4)',
          }}
        >
          {copied ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          )}
          {copied ? 'COPIED!' : 'COPY RESULTS'}
        </button>

        <button
          onClick={onPlayAgain}
          className="px-10 py-4 rounded-full text-lg font-bold transition-all duration-200 hover:scale-105 active:scale-95"
          style={{
            backgroundColor: 'var(--rush-purple-mid)',
            color: '#fff',
            boxShadow: 'var(--rush-shadow-glow)',
          }}
        >
          <span className="flex items-center justify-center gap-2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="6,3 20,12 6,21" />
            </svg>
            PLAY AGAIN
          </span>
        </button>
      </div>

      {lastMetricName && METRIC_KEY_MAP[lastMetricName] && METRIC_DEFINITIONS[METRIC_KEY_MAP[lastMetricName]] && (
        <div
          className="mt-6 w-full max-w-xs rounded-xl px-4 py-3 text-left text-sm leading-relaxed"
          style={{
            backgroundColor: 'rgba(96, 165, 250, 0.12)',
            border: '1px solid rgba(96, 165, 250, 0.25)',
            color: 'hsl(var(--muted-foreground))',
          }}
        >
          <span style={{ color: 'rgb(96, 165, 250)', fontWeight: 700 }}>TIP: </span>
          <strong>{lastMetricName}</strong> — {METRIC_DEFINITIONS[METRIC_KEY_MAP[lastMetricName]].explanation}
        </div>
      )}

      {hasIndustryContext && lastQuestion && (
        <div
          className="mt-4 w-full max-w-xs rounded-xl px-4 py-3 text-left text-sm leading-relaxed"
          style={{
            backgroundColor: 'rgba(168, 85, 247, 0.10)',
            border: '1px solid rgba(168, 85, 247, 0.25)',
            color: 'hsl(var(--muted-foreground))',
          }}
        >
          <span style={{ color: 'rgb(168, 85, 247)', fontWeight: 700 }}>INDUSTRY INSIGHT</span>
          {industryContextA && (
            <p className="mt-2">
              <strong style={{ color: 'hsl(var(--foreground))' }}>{lastQuestion.stockA.name}</strong>
              {lastQuestion.stockA.subIndustry && (
                <span style={{ color: 'var(--rush-text-muted)', fontWeight: 400 }}>{' '}({lastQuestion.stockA.subIndustry})</span>
              )}
              {' '}&mdash; {industryContextA}
            </p>
          )}
          {industryContextB && (
            <p className="mt-2">
              <strong style={{ color: 'hsl(var(--foreground))' }}>{lastQuestion.stockB.name}</strong>
              {lastQuestion.stockB.subIndustry && (
                <span style={{ color: 'var(--rush-text-muted)', fontWeight: 400 }}>{' '}({lastQuestion.stockB.subIndustry})</span>
              )}
              {' '}&mdash; {industryContextB}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
